import Scrollspy from 'react-scrollspy-ez';
import styled from 'styled-components';

import BadgeStyled from '../../../components/Badge';
import ContentHeaderStyled from '../../../components/ContentHeader';
import ExternalLinkGroupStyled from '../../../components/ExternalLinkGroup';
import PaginationStyled from '../../../components/Pagination';
import { REFERENCE_LINKS } from '../../../constants';
import { IconDanger, IconSuccess } from '../../../core';
import commonPageContentStyles from '../../../styles/mixins/_commonPageContentStyles';
import navigationStyles from '../../../styles/mixins/navigationMenuStyles';

interface Props {
  className?: string;
}

const dataExternalLinkGroups = [
  {
    id: 1,
    title: 'Government accessibility resources',
    cards: [
      {
        title: 'The Section 508 accessibility program',
        subtitle: '[section508.gov]',
        linkUrl: REFERENCE_LINKS.section508Program,
      },
      {
        title: 'Accessibility for Teams guide',
        subtitle: '[digital.gov]',
        linkUrl: REFERENCE_LINKS.digitalGovAccessibilityForTeams,
      },
      {
        title: 'Find your Section 508 Coordinator',
        subtitle: '[section508.gov]',
        linkUrl: REFERENCE_LINKS.section508ProgramManagerListing,
      },
    ],
  },
];

const SelectionStructure = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="content">
      <ContentHeaderStyled
        title="Selection"
        breadcrumbs={[
          { label: 'General Components', url: '/general-components' },
          { label: 'Selection', url: '/' },
        ]}
      />
      <div className="page-container">
        <div className="page-content">
          <div className="page-content-with-navigation-wrapper">
            <div className="page-content-with-navigation">
              <div className="page-content-item">
                <h2 className="h1" id="overview">
                  Overview
                </h2>
                <h3 className="h3" id="filtering">
                  Filtering
                </h3>
                <p className="mb-40">
                  Filtering allows users to refine options and narrow down their choices based on
                  specific criteria. It typically involves using dropdowns, checkboxes, or search
                  fields that enable users to filter content according to defined attributes.
                  Filters should be clearly labeled and accessible, allowing users to easily toggle
                  or apply them to view relevant selections. This enhances user experience by making
                  it faster to find what they need while maintaining a clean, organized interface.
                </p>
                <h3 className="h3" id="selection-types">
                  Selection types
                </h3>
                <p className="mb-8">
                  Selection types allow users to choose one or more options from a set of available
                  choices, catering to different interaction needs. A single-selection filter is
                  used when the user can pick only one attribute to modify data results, behaving
                  like a radio button. Common types of single-selection filters include dropdowns,
                  radio buttons, and segmented buttons.
                </p>
                <p className="mb-40">
                  On the other hand, multi-selection filters allow users to select multiple options
                  simultaneously, useful when the user needs to modify or filter by more than one
                  attribute. Examples include checkboxes and multi-select dropdowns. These types
                  ensure that the interface remains intuitive and efficient, meeting the user&apos;s
                  selection requirements.
                </p>
              </div>
              <table className="table table-variants text-h5 mb-40">
                <thead>
                  <tr>
                    <th style={{ width: '15.625rem' }}>Selection method</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Single selection</td>
                    <td>The user can pick only one attribute to modify data results.</td>
                  </tr>
                  <tr>
                    <td>Multiselection</td>
                    <td>
                      The user has the option to pick more than one attribute to modify data
                      <br />
                      results.
                    </td>
                  </tr>
                  <tr>
                    <td>Multiple categories</td>
                    <td>
                      The user has the option to select attributes across multiple data categories.
                    </td>
                  </tr>
                  <tr>
                    <td>
                      Multiple filters with batch
                      <br />
                      updates
                    </td>
                    <td>
                      The user selects multiple filters and then takes an additional action to apply
                      <br />
                      the filters.
                    </td>
                  </tr>
                  <tr>
                    <td>
                      Multiple filters with instant
                      <br />
                      updates
                    </td>
                    <td>The data is updated as the user selects each filter.</td>
                  </tr>
                </tbody>
              </table>

              <div className="page-content-item">
                <h4 className="h4 text-h4-sb">Single selection</h4>
                <p className="mb-16">
                  Single selection filters allow users to choose only one option from a set of
                  available choices. This type of filter is ideal when the user needs to make a
                  distinct choice or modify data results based on a single attribute. Common
                  components used for single selection include radio buttons, dropdowns, and
                  segmented buttons. These elements are typically used when only one option is
                  applicable, providing clarity and ensuring that users can make their decision
                  quickly and accurately. Types of single-selection filters include:
                </p>
                <ul className="disc-list font-sm">
                  <li>Basic dropdown</li>
                  <li>Inline dropdown</li>
                  <li>Radio button set (either standalone or within a menu)</li>
                </ul>
              </div>
              <figure className="figure-sm">
                <img
                  className="img-responsive"
                  src="/images/general-components-selection/screen-single-selection@2x.webp"
                  width={1000}
                  height={556}
                  alt=""
                />
                <figcaption>Example of a single selection filter in an inline dropdown.</figcaption>
              </figure>
              <div className="page-content-item">
                <h4 className="h4 text-h4-sb">Multiselect</h4>
                <p className="mb-16">
                  Multiselect filters allow users to select multiple options from a list of choices,
                  offering flexibility when modifying data results based on multiple attributes.
                  This type of filter is ideal for scenarios where more than one option can apply at
                  once. Common components used for multiselect include checkboxes, multiselect
                  dropdowns, and tag-based selection. These elements enable users to easily pick
                  several options without limitation, helping them refine their results with greater
                  precision. Types of multiselect filters include:
                </p>
                <ul className="disc-list font-sm">
                  <li>Multiselect dropdown</li>
                  <li>Inline multiselect dropdown</li>
                  <li>Checkbox set (either standalone or within a menu)</li>
                </ul>
              </div>
              <figure className="figure-sm">
                <img
                  className="img-responsive"
                  src="/images/general-components-selection/screen-multiselect@2x.webp"
                  width={1000}
                  height={510}
                  alt=""
                />
                <figcaption>Example of filter without selections.</figcaption>
              </figure>
              <div className="page-content-item">
                <p className="mb-40">
                  If the filter(s) can be hidden in either a drawer, dropdown, or menu, then there
                  should be an indicator visible on the closed filter state that informs the user
                  that filters have been applied. At a minimum, the indicator should include the
                  number of filters applied and have the option to clear filters without re-opening
                  the filter container.
                </p>
              </div>
              <h2 className="h1 divider" id="specifications">
                Specifications
              </h2>
              <div className="page-content-item">
                <h3 className="h3" id="select-overview">
                  Select Overview
                </h3>
                <p className="mb-40">
                  The Select component is used to provide users with a dropdown list of options from
                  which they can choose a single or multiple items. It offers an intuitive way to
                  filter or modify data, with clear visibility and ease of interaction. Select
                  components are ideal for cases where space is limited, as they condense many
                  options into a compact format. They can be used for both single and multiple
                  selections, depending on the user&apos;s needs. Key considerations include the
                  clear display of options, proper labeling, and maintaining accessibility to ensure
                  a smooth user experience.
                </p>
              </div>
              <figure className="figure-sm">
                <img
                  className="img-responsive"
                  src="/images/general-components-selection/select-overview@2x.webp"
                  width={1000}
                  height={524}
                  alt=""
                />
                <figcaption>Example of filter without selections.</figcaption>
              </figure>
              <div className="page-content-item">
                <h3 className="h3 mb-40" id="states">
                  States
                </h3>
              </div>
              <table className="table table-variants text-h5 mb-40">
                <thead>
                  <tr>
                    <th style={{ width: '15.625rem' }}>State</th>
                    <th>Element</th>
                    <th>Property</th>
                    <th>Color token</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>1. Default</td>
                    <td>Field</td>
                    <td>background-color</td>
                    <td>
                      <div className="role-color">$primary/bg-base</div>
                    </td>
                  </tr>
                  <tr>
                    <td>2. Hover</td>
                    <td>Field</td>
                    <td>background-color</td>
                    <td>
                      <div className="role-color">$neutral/bg-hover</div>
                    </td>
                  </tr>
                  <tr>
                    <td>3. Pressed</td>
                    <td>Field</td>
                    <td>background-color</td>
                    <td>
                      <div className="role-color">$neutral/bg-active</div>
                    </td>
                  </tr>
                  <tr>
                    <td>4. Disabled</td>
                    <td>Field</td>
                    <td>background-color</td>
                    <td>
                      <div className="role-color">none</div>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div className="number-cards row-gap-24">
                <div className="number-card">
                  <div className="number-card-img">
                    <span className="number-circle">1</span>
                    <img
                      src="/images/general-components-selection/screen-states-01@2x.webp"
                      width={488}
                      height={278}
                      alt=""
                    />
                  </div>
                </div>
                <div className="number-card">
                  <div className="number-card-img">
                    <span className="number-circle">2</span>
                    <img
                      src="/images/general-components-selection/screen-states-02@2x.webp"
                      width={488}
                      height={278}
                      alt=""
                    />
                  </div>
                </div>
                <div className="number-card">
                  <div className="number-card-img">
                    <span className="number-circle">3</span>
                    <img
                      src="/images/general-components-selection/screen-states-03@2x.webp"
                      width={488}
                      height={278}
                      alt=""
                    />
                  </div>
                </div>
                <div className="number-card">
                  <div className="number-card-img">
                    <span className="number-circle">4</span>
                    <img
                      src="/images/general-components-selection/screen-states-04@2x.webp"
                      width={488}
                      height={278}
                      alt=""
                    />
                  </div>
                </div>
              </div>
              <h2 className="h1 divider" id="guidelines">
                Guidelines
              </h2>
              <div className="page-content-item">
                <h3 className="h3" id="content">
                  Content
                </h3>
                <p className="mb-40 text-paragraph-medium">
                  When providing content for Select components, give a short, clear description (1-3
                  words) of the requested input. Use sentence case, capitalizing the first word and
                  leaving the rest lowercase. Avoid punctuation and articles like “the,” “an,” or
                  “a.” Each description should be an independent sentence and not rely on the
                  options in the Select component for completion, ensuring support for
                  internationalization. Descriptions should be descriptive, not instructional—if
                  further explanation is needed, use help text below the field. This approach
                  maintains clarity and accessibility for users across different contexts.
                </p>
                <div className="visually-hidden" aria-hidden="true" id="radio-button">
                  Radio button
                </div>
                <h3 className="h3">Radio button usage</h3>
                <p className="mb-40">
                  Radio buttons should be used when only one option can be selected from a set of
                  mutually exclusive choices. Each option should be clearly labeled, and the choices
                  should be logically grouped to avoid confusion. Ensure that the default selection
                  is appropriate, and only one radio button can be selected at a time. Radio buttons
                  should not be used for more than 7 options to maintain clarity and prevent
                  overwhelming the user. If more options are needed, consider alternative selection
                  methods, such as a dropdown or multi-select component.
                </p>
              </div>
              <div className="reccomendations mb-40">
                <div className="reccomendation-item">
                  <BadgeStyled className="success" iconLeft={<IconSuccess />}>
                    Do
                  </BadgeStyled>
                  <ul className="disc-list">
                    <li>Email address</li>
                    <li>Phone number</li>
                  </ul>
                </div>
                <div className="reccomendation-item">
                  <BadgeStyled className="danger" iconLeft={<IconDanger />}>
                    Don&apos;t
                  </BadgeStyled>
                  <ul className="disc-list">
                    <li>What is your email address?</li>
                    <li>My phone number is:</li>
                  </ul>
                </div>
              </div>
              <img
                className="img-responsive mb-40"
                src="/images/general-components-selection/screen-radio-button@2x.webp"
                width={1000}
                height={558}
                alt=""
              />
              <div className="page-content-item">
                <h4 className="h4 text-h4-sb">When not to use</h4>
                <p className="mb-40">
                  If a user can select from multiple options, use checkboxes instead of radio
                  buttons. Radio buttons allow the user to select only one item in a set whereas
                  checkboxes allow the user to select multiple items.
                </p>
              </div>
              <div className="ex-cards">
                <div className="ex-card true">
                  <img
                    className="ex-card-img"
                    src="/images/general-components-selection/screen-radio-button-do@2x.webp"
                    width={488}
                    height={354}
                    alt=""
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="success" iconLeft={<IconSuccess />}>
                      Do
                    </BadgeStyled>
                    <p>Do use radio buttons when only one item can be selected.</p>
                  </div>
                </div>
                <div className="ex-card false">
                  <img
                    className="ex-card-img"
                    src="/images/general-components-selection/screen-radio-button-dont@2x.webp"
                    width={488}
                    height={354}
                    alt=""
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="danger" iconLeft={<IconDanger />}>
                      Don&apos;t
                    </BadgeStyled>
                    <p>Don&apos;t use checkboxes when only one item can be selected.</p>
                  </div>
                </div>
              </div>
              <div className="page-content-item">
                <div className="visually-hidden" aria-hidden="true" id="checkbox">
                  Checkbox
                </div>
                <h3 className="h3">Checkbox usage</h3>
                <p className="mb-40">
                  Checkboxes should be used when allowing users to select one or more options from a
                  set of choices. Each checkbox should represent an independent option, and users
                  should be able to check or uncheck them freely. Avoid using checkboxes for
                  mutually exclusive options, as radio buttons are more appropriate in those cases.
                  Ensure that the labels are clear and concise, and if there are many options, group
                  them logically to help users make informed decisions. If a checkbox is part of a
                  form, consider providing a default state (checked or unchecked) based on the
                  context of the form.
                </p>
              </div>
              <img
                className="img-responsive mb-40"
                src="/images/general-components-selection/screen-checkbox-usage@2x.webp"
                width={1000}
                height={558}
                alt=""
              />
              <div className="page-content-item">
                <h4 className="h4 text-h4-sb">When not to use</h4>
                <p className="mb-40">
                  Avoid using checkboxes when only one option is needed (use radio buttons instead),
                  when options are mutually exclusive, or when the choice can be better represented
                  with other UI components like dropdowns or toggles. Also, limit the number of
                  checkboxes to prevent overwhelming the user.
                </p>
              </div>
              <div className="ex-cards">
                <div className="ex-card true">
                  <img
                    className="ex-card-img"
                    src="/images/general-components-selection/screen-use-checkboxes@2x.webp"
                    width={488}
                    height={354}
                    alt=""
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="success" iconLeft={<IconSuccess />}>
                      Do
                    </BadgeStyled>
                    <p>Do use checkboxes when multiple items can be selected.</p>
                  </div>
                </div>
                <div className="ex-card false">
                  <img
                    className="ex-card-img"
                    src="/images/general-components-selection/screen-use-radio-buttons@2x.webp"
                    width={488}
                    height={354}
                    alt=""
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="danger" iconLeft={<IconDanger />}>
                      Don&apos;t
                    </BadgeStyled>
                    <p>Don&apos;t use radio buttons when multiple items can be selected.</p>
                  </div>
                </div>
              </div>
              <h2 className="h1 divider" id="references">
                References
              </h2>
              {dataExternalLinkGroups.map((externalLinkGroup) => (
                <ExternalLinkGroupStyled
                  key={externalLinkGroup.id}
                  title={externalLinkGroup.title}
                  cards={externalLinkGroup.cards}
                />
              ))}
            </div>
            <div>
              <div className="page-navigation">
                <h2>Contents</h2>
                <Scrollspy
                  ids={[
                    'overview',
                    'filtering',
                    'selection-types',
                    'specifications',
                    'select-overview',
                    'states',
                    'guidelines',
                    'content',
                    'radio-button',
                    'checkbox',
                    'references',
                  ]}
                  offset={0}
                  activeItemClassName="active"
                  itemContainerClassName="multilevel-link-list"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <PaginationStyled
        previousLink="/general-components/navigation"
        previous="Navigation"
        nextLink="/general-components/containment"
        next="Containment"
      />
    </div>
  </div>
);

const Selection = styled(SelectionStructure)`
  ${commonPageContentStyles}
  ${navigationStyles([2, 3, 5, 6, 8, 9, 10])}

    overflow: unset;
  height: 100%;

  > .content {
    width: 100%;
    padding: 0;
  }
`;

export default Selection;
